/**
 * OIDC Authentication Provider
 *
 * Wraps the app with Authentik OIDC authentication via react-oidc-context.
 * Configured for the ix-fabric-api application in Authentik.
 */

import { ReactNode } from 'react';
import {
  AuthProvider as OidcAuthProvider,
  AuthContext,
  type AuthContextProps,
  type AuthProviderProps,
} from 'react-oidc-context';
import { WebStorageStateStore } from 'oidc-client-ts';
import { getOidcAuthority, getOidcClientId, isDevSessionAuth, OIDC_FILTERED_PROTOCOL_CLAIMS } from '../lib/config';

// OIDC Configuration for Authentik
// Authority/client_id come from config (VITE_OIDC_AUTHORITY / VITE_OIDC_CLIENT_ID)
// so the same bundle runs against the legacy or Pharos issuer (CDEV-376).
const oidcConfig: AuthProviderProps = {
  authority: getOidcAuthority(),
  client_id: getOidcClientId(),
  redirect_uri: `${window.location.origin}/callback`,
  post_logout_redirect_uri: window.location.origin,
  scope: 'openid profile email groups',

  // Keep auth_time in the profile: see OIDC_FILTERED_PROTOCOL_CLAIMS.
  filterProtocolClaims: OIDC_FILTERED_PROTOCOL_CLAIMS,

  // Use localStorage to persist auth state across browser sessions
  userStore: new WebStorageStateStore({ store: window.localStorage }),

  // Automatically renew tokens before expiry
  automaticSilentRenew: true,

  // Timeout for silent renewal iframe (seconds).
  // Without this, signinSilent() hangs forever when the IdP session has expired
  // and Authentik redirects the iframe to the login page instead of returning
  // error=login_required to the redirect_uri.
  silentRequestTimeoutInSeconds: 10,

  // Handle the redirect callback automatically
  onSigninCallback: () => {
    // Remove the OIDC state from the URL after login
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// CDEV-404: dev-only session-key auth context
//
// When VITE_DEV_SESSION_KEY is set in a development build (isDevSessionAuth()),
// we bypass OIDC entirely and provide a pre-authenticated react-oidc-context
// value. This is what makes the *whole* app behave as logged in — Shell's
// redirect logic AND every `enabled: auth.isAuthenticated` query gate. The
// actual fabric authentication is the bare session keyname the GraphQL client
// sends (see lib/graphql/client.ts); the access_token here is unused.
//
// Hard-gated by isDevSessionAuth() → import.meta.env.DEV, so this context can
// never be constructed in a production build.
// ─────────────────────────────────────────────────────────────────────────────
const noop = async () => {};
const DEV_AUTH_CONTEXT = {
  isLoading: false,
  isAuthenticated: true,
  activeNavigator: undefined,
  error: undefined,
  user: {
    access_token: 'dev-session-key',
    token_type: 'session-key',
    profile: {
      sub: 'sec:service-claude-code',
      email: 'dev session (claude-code)',
      name: 'Dev Session',
      preferred_username: 'claude-code',
      groups: ['ix-admins'],
    },
    expires_at: Math.floor(Date.now() / 1000) + 365 * 24 * 60 * 60,
  },
  settings: {} as never,
  events: {} as never,
  signinRedirect: noop,
  signinResourceOwnerCredentials: noop,
  signinPopup: noop,
  signinSilent: async () => null,
  signinCallback: async () => undefined,
  signoutRedirect: noop,
  signoutPopup: noop,
  signoutSilent: noop,
  signoutCallback: async () => undefined,
  removeUser: noop,
  clearStaleState: noop,
  querySessionStatus: async () => null,
  revokeTokens: noop,
  startSilentRenew: () => {},
  stopSilentRenew: () => {},
} as unknown as AuthContextProps;

interface AuthProviderComponentProps {
  children: ReactNode;
}

/**
 * AuthProvider - Wraps children with OIDC authentication context
 *
 * Usage:
 * ```tsx
 * import { useAuth } from 'react-oidc-context';
 *
 * function MyComponent() {
 *   const auth = useAuth();
 *
 *   if (auth.isLoading) return <div>Loading...</div>;
 *   if (auth.error) return <div>Error: {auth.error.message}</div>;
 *
 *   if (auth.isAuthenticated) {
 *     return <div>Hello {auth.user?.profile.email}</div>;
 *   }
 *
 *   return <button onClick={() => auth.signinRedirect()}>Login</button>;
 * }
 * ```
 */
export function AuthProvider({ children }: AuthProviderComponentProps) {
  // CDEV-404: dev session-key mode short-circuits OIDC with a pre-authenticated
  // context so automated agents can verify the app locally without the IdP hop.
  if (isDevSessionAuth()) {
    return <AuthContext.Provider value={DEV_AUTH_CONTEXT}>{children}</AuthContext.Provider>;
  }

  return (
    <OidcAuthProvider {...oidcConfig}>
      {children}
    </OidcAuthProvider>
  );
}

export default AuthProvider;
